import type { ExportParticipantRequest } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FaChartLine, FaFileAlt, FaFileArchive, FaStickyNote, FaUser } from 'react-icons/fa';
import { HookedCheckboxStack } from '../../components/forms/HookedCheckboxStack';
import { HookedFormFieldset } from '../../components/forms/HookedFormFieldset';
import { HookedSubmitButton } from '../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../components/generic/Modal';
import { zodResolver } from '../../utils/zod';

type FormValues = {
  options: (keyof ExportParticipantRequest)[];
};

const schema: z.ZodSchema<FormValues> = z.object({
  options: z
    .array(z.enum(['profile', 'behaviourRecordings', 'notes', 'documents', 'files']))
    .min(1, 'At least one item is required'),
});

interface Props {
  onExport: (request: ExportParticipantRequest) => void;
}

export const ParticipantExportModal = createModal<Props>((props) => {
  const { modal, onExport } = props;
  const { hide, remove, visible } = modal;

  const initialValues: FormValues = {
    options: [],
  };

  const methods = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(schema),
  });

  const onSubmit = (values: FormValues) => {
    hide();
    onExport({
      profile: values.options.includes('profile'),
      behaviourRecordings: values.options.includes('behaviourRecordings'),
      notes: values.options.includes('notes'),
      documents: values.options.includes('documents'),
      files: values.options.includes('files'),
    });
  };

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Export participant'
                subtitle='Choose what to include in the export'
              />
              <ChakraV3.Dialog.Body>
                <HookedFormFieldset name='options'>
                  <HookedCheckboxStack
                    name='options'
                    options={[
                      {
                        label: 'Profile',
                        value: 'profile',
                        description:
                          'Personal and contact details, key contacts and custom fields, as a spreadsheet (CSV)',
                        icon: <FaUser />,
                      },
                      {
                        label: 'Behaviour recordings',
                        value: 'behaviourRecordings',
                        description: 'Behaviour recording data, as a spreadsheet (CSV)',
                        icon: <FaChartLine />,
                      },
                      {
                        label: 'Notes',
                        value: 'notes',
                        description: 'Every note, each as its own PDF',
                        icon: <FaStickyNote />,
                      },
                      {
                        label: 'Documents',
                        value: 'documents',
                        description: 'Every document, each as its own PDF',
                        icon: <FaFileAlt />,
                      },
                      {
                        label: 'Files',
                        value: 'files',
                        description: 'Every uploaded file, in its original format',
                        icon: <FaFileArchive />,
                      },
                    ]}
                  />
                </HookedFormFieldset>
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Export</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
